import React from "react";

import { ApiProvider } from "./api/api-provider";
import type { AuthStrategy } from "./api/auth-strategy";
import { resetAuthStrategy, setAuthStrategy } from "./api/auth-strategy";
import {
  buildWebsocketUrl,
  getFiinderApi,
  initWebSocket,
} from "./api/fiinder-api";
import type { FiinderApi } from "./api/fiinder-api-types";
import {
  clearWebsocketConfiguration,
  configureWebsocket,
} from "./api/websocket-config";
import { PersonationContext } from "@/lib/personation-context";
import {
  assignGlobals,
  toPersonationValue,
  type PersonationProps,
} from "@/components/providers/personation";

const REFRESH_MARGIN_SECONDS = 5;

type SessionTokens = {
  accessToken: string;
  expiresIn: number;
};

type ActiveSession = SessionTokens & {
  expiresAt: number;
};

type EcPublicJwk = {
  kty: "EC";
  crv: string;
  x: string;
  y: string;
  [key: string]: string;
};

type DpopPublicJwk = EcPublicJwk & {
  alg: string;
  use: string;
};

type DpopKeyPair = {
  privateKey: CryptoKey;
  publicJwk: DpopPublicJwk;
  thumbprint: string;
};

const bootstrapPromiseRegistry = new Map<string, Promise<ActiveSession | null>>();
const refreshPromiseRegistry = new Map<string, Promise<ActiveSession | null>>();

type SessionBasedFiinderProviderProps = Omit<
  PersonationProps,
  "companyToken"
> & {
  children: React.ReactNode;
  sessionServerUrl: string;
  tenantId: string;
  userId: string;
};

type SessionResponse = {
  access_token: string;
  expires_in: number;
  [key: string]: unknown;
};

type DpopProofArgs = {
  htu: string;
  htm: string;
  accessToken?: string;
};

const TEXT_ENCODER = new TextEncoder();

class ThumbprintMismatchError extends Error {
  expectedThumbprint: string;
  actualThumbprint: string;

  constructor(expected: string, actual: string) {
    super(
      `[SessionBasedFiinderProvider] Token thumbprint (${expected}) does not match local key thumbprint (${actual})`
    );
    this.name = "ThumbprintMismatchError";
    this.expectedThumbprint = expected;
    this.actualThumbprint = actual;
  }
}

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

function normalizeBaseUrl(url: string): string {
  return url.replace(/\/+$/u, "");
}

function joinUrl(baseUrl: string, path: string): string {
  const normalizedBase = normalizeBaseUrl(baseUrl);
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${normalizedBase}${normalizedPath}`;
}

function base64urlEncode(input: string | ArrayBuffer | Uint8Array): string {
  let source: string;
  if (typeof input === "string") {
    source = input;
  } else {
    const bytes = input instanceof ArrayBuffer ? new Uint8Array(input) : input;
    let binary = "";
    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });
    source = binary;
  }
  return btoa(source)
    .replace(/=+$/u, "")
    .replace(/\+/gu, "-")
    .replace(/\//gu, "_");
}

async function hashAccessToken(token: string): Promise<string> {
  if (!isBrowser() || !window.crypto?.subtle) {
    throw new Error("Web Crypto API is not available for hashing");
  }
  const digest = await window.crypto.subtle.digest(
    "SHA-256",
    TEXT_ENCODER.encode(token)
  );
  return base64urlEncode(digest);
}

function normalizeBase64Url(value: string): string {
  const normalized = value.replace(/-/gu, "+").replace(/_/gu, "/");
  const pad = normalized.length % 4;
  return pad === 0 ? normalized : normalized + "=".repeat(4 - pad);
}

function base64UrlDecode(value: string): string {
  const normalized = normalizeBase64Url(value);
  if (typeof window !== "undefined" && typeof window.atob === "function") {
    return window.atob(normalized);
  }
  if (typeof globalThis !== "undefined") {
    const anyGlobal = globalThis as unknown as { atob?: (input: string) => string }; // fallback
    if (typeof anyGlobal.atob === "function") {
      return anyGlobal.atob(normalized);
    }
  }
  const maybeBuffer = (globalThis as { Buffer?: { from: (input: string, encoding: string) => { toString: (enc: string) => string } } }).Buffer;
  if (maybeBuffer) {
    return maybeBuffer.from(normalized, "base64").toString("utf-8");
  }
  throw new Error("No base64 decoder available in this environment");
}

function decodeJwtPayload<T = any>(token: string): T | null {
  const segments = token.split(".");
  if (segments.length < 2) return null;
  try {
    const decoded = base64UrlDecode(segments[1]);
    return JSON.parse(decoded) as T;
  } catch (error) {
    console.error("[SessionBasedFiinderProvider] Failed to decode JWT payload", error);
    return null;
  }
}

function extractTokenThumbprint(token: string): string | null {
  const payload = decodeJwtPayload<{ cnf?: { jkt?: string } }>(token);
  if (!payload) return null;
  const jkt = payload.cnf?.jkt;
  return typeof jkt === "string" && jkt.length > 0 ? jkt : null;
}

async function computeJwkThumbprint(jwk: EcPublicJwk): Promise<string> {
  const method = jwk.kty;
  let canonical: string;

  if (method === "EC") {
    if (!jwk.crv || !jwk.x || !jwk.y) {
      throw new Error("Invalid EC JWK: missing crv, x, or y");
    }
    canonical = JSON.stringify({
      crv: jwk.crv,
      kty: "EC",
      x: jwk.x,
      y: jwk.y,
    });
  } else if (method === "RSA") {
    const rsaJwk = jwk as EcPublicJwk & { n?: string; e?: string };
    if (!rsaJwk.n || !rsaJwk.e) {
      throw new Error("Invalid RSA JWK: missing n or e");
    }
    canonical = JSON.stringify({
      e: rsaJwk.e,
      kty: "RSA",
      n: rsaJwk.n,
    });
  } else if (method === "OKP") {
    const okpJwk = jwk as EcPublicJwk & { x?: string; crv?: string };
    if (!okpJwk.x || !okpJwk.crv) {
      throw new Error("Invalid OKP JWK: missing crv or x");
    }
    canonical = JSON.stringify({
      crv: okpJwk.crv,
      kty: "OKP",
      x: okpJwk.x,
    });
  } else {
    throw new Error(`Unsupported JWK type: ${method}`);
  }

  const digest = await window.crypto.subtle.digest(
    "SHA-256",
    TEXT_ENCODER.encode(canonical)
  );
  return base64urlEncode(digest);
}

export function SessionBasedFiinderProvider(
  props: SessionBasedFiinderProviderProps
) {
  const keyPairRef = React.useRef<DpopKeyPair | null>(null);
  const refreshTimerRef = React.useRef<number | null>(null);
  const bootstrapPromiseRef = React.useRef<Promise<ActiveSession | null> | null>(
    null
  );
  const refreshPromiseRef = React.useRef<Promise<ActiveSession | null> | null>(
    null
  );
  const bootstrapKey = React.useMemo(
    () =>
      [
        normalizeBaseUrl(props.sessionServerUrl.trim()),
        props.tenantId.trim(),
        props.userId.trim(),
      ].join("::"),
    [props.sessionServerUrl, props.tenantId, props.userId]
  );

  React.useEffect(() => {
    const existingBootstrap = bootstrapPromiseRegistry.get(bootstrapKey) ?? null;
    bootstrapPromiseRef.current = existingBootstrap;
    const existingRefresh = refreshPromiseRegistry.get(bootstrapKey) ?? null;
    refreshPromiseRef.current = existingRefresh;
    return () => {
      if (!bootstrapPromiseRef.current) {
        bootstrapPromiseRegistry.delete(bootstrapKey);
      }
      if (!refreshPromiseRef.current) {
        refreshPromiseRegistry.delete(bootstrapKey);
      }
    };
  }, [bootstrapKey]);
  const sessionRef = React.useRef<ActiveSession | null>(null);
  const isMountedRef = React.useRef(true);
  const [session, setSession] = React.useState<ActiveSession | null>(null);

  const normalizedSessionServerUrl = React.useMemo(
    () => normalizeBaseUrl(props.sessionServerUrl.trim()),
    [props.sessionServerUrl]
  );

  const trimmedFiinderApiUrl = React.useMemo(
    () => props.fiinderApiUrl.trim(),
    [props.fiinderApiUrl]
  );

  const normalizedFiinderApiUrl = React.useMemo(
    () => (trimmedFiinderApiUrl ? normalizeBaseUrl(trimmedFiinderApiUrl) : ""),
    [trimmedFiinderApiUrl]
  );

  const ensureKeyPair = React.useCallback(async (): Promise<DpopKeyPair> => {
    if (!isBrowser() || !window.crypto?.subtle) {
      throw new Error("Web Crypto API is not available");
    }
    if (keyPairRef.current) {
      return keyPairRef.current;
    }
    const keyPair = await window.crypto.subtle.generateKey(
      { name: "ECDSA", namedCurve: "P-256" },
      true,
      ["sign", "verify"]
    );
    const exported = (await window.crypto.subtle.exportKey(
      "jwk",
      keyPair.publicKey
    )) as JsonWebKey;
    delete (exported as Record<string, unknown>).key_ops;
    delete (exported as Record<string, unknown>).ext;

    const { crv, kty, x, y } = exported as {
      crv?: string;
      kty?: string;
      x?: string;
      y?: string;
    };

    if (!crv || !kty || !x || !y) {
      throw new Error(
        "[SessionBasedFiinderProvider] Exported JWK missing required fields"
      );
    }

    const publicJwk: DpopPublicJwk = Object.freeze({
      crv,
      kty: kty as "EC",
      x,
      y,
      alg: "ES256",
      use: "sig",
    });

    const thumbprint = await computeJwkThumbprint(publicJwk);

    const result: DpopKeyPair = {
      privateKey: keyPair.privateKey,
      publicJwk,
      thumbprint,
    };
    keyPairRef.current = result;
    return result;
  }, []);

  const createDpopProof = React.useCallback(
    async ({ htu, htm, accessToken }: DpopProofArgs) => {
      const keyPair = await ensureKeyPair();
      const header = {
        alg: "ES256",
        typ: "dpop+jwt",
        jwk: keyPair.publicJwk,
      };
      const payload: Record<string, unknown> = {
        htu,
        htm,
        jti: crypto.randomUUID(),
        iat: Math.floor(Date.now() / 1000),
      };
      if (accessToken) {
        payload.ath = await hashAccessToken(accessToken);
      }
      const signingInput = `${base64urlEncode(
        JSON.stringify(header)
      )}.${base64urlEncode(JSON.stringify(payload))}`;
      const signature = await window.crypto.subtle.sign(
        { name: "ECDSA", hash: "SHA-256" },
        keyPair.privateKey,
        TEXT_ENCODER.encode(signingInput)
      );
      return `${signingInput}.${base64urlEncode(signature)}`;
    },
    [ensureKeyPair]
  );

  const clearRefreshTimer = React.useCallback(() => {
    if (refreshTimerRef.current !== null) {
      window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const applySessionResponse = React.useCallback(
    async (data: SessionResponse) => {
      if (!isMountedRef.current) return null;
      const accessToken = data.access_token;
      const expiresInSeconds = Number(data.expires_in);
      if (!accessToken || !Number.isFinite(expiresInSeconds)) {
        throw new Error("Session response missing access_token or expires_in");
      }

      const keyPair = await ensureKeyPair();
      const tokenThumbprint = extractTokenThumbprint(accessToken);
      if (
        tokenThumbprint &&
        keyPair.thumbprint &&
        tokenThumbprint !== keyPair.thumbprint
      ) {
        throw new ThumbprintMismatchError(
          tokenThumbprint,
          keyPair.thumbprint
        );
      }

      const strategy: AuthStrategy = {
        async getHeaders({ url, method }) {
          const dpop = await createDpopProof({
            htu: url,
            htm: method,
            accessToken,
          });
          return {
            Authorization: `DPoP ${accessToken}`,
            DPoP: dpop,
          };
        },
      };
      setAuthStrategy(strategy);
      configureWebsocket({
        async getCredentials() {
          const websocketHtu = buildWebsocketUrl(accessToken);
          const websocketProof = await createDpopProof({
            htu: websocketHtu,
            htm: "GET",
            accessToken,
          });
          return {
            accessToken,
            dpopProof: websocketProof,
          };
        },
      });
      if (typeof window !== "undefined") {
        window.FIINDER_COMPANY_TOKEN = accessToken;
      }

      const expiresAt = Date.now() + Math.max(0, expiresInSeconds) * 1000;
      const nextSession: ActiveSession = {
        accessToken,
        expiresIn: expiresInSeconds,
        expiresAt,
      };
      sessionRef.current = nextSession;
      setSession(nextSession);
      return nextSession;
    },
    [createDpopProof, ensureKeyPair]
  );

  const callSessionEndpoint = React.useCallback(
    async (
      path: string,
      method: "POST" | "GET",
      options?: {
        body?: Record<string, unknown>;
        accessTokenForProof?: string;
        overrideBaseUrl?: string;
        htuOverride?: string;
      }
    ) => {
      const {
        body,
        accessTokenForProof,
        overrideBaseUrl,
        htuOverride,
      } = options ?? {};
      const baseUrl = overrideBaseUrl
        ? normalizeBaseUrl(overrideBaseUrl)
        : normalizedSessionServerUrl;
      const url = joinUrl(baseUrl, path);
      const proofHtu = htuOverride ?? url;
      const dpop = await createDpopProof({
        htu: proofHtu,
        htm: method,
        accessToken: accessTokenForProof,
      });
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          DPoP: dpop,
        },
        credentials: "include",
        body: body ? JSON.stringify(body) : undefined,
      });
      const text = await response.text();
      if (!response.ok) {
        throw new Error(
          text || `Request failed with status ${response.status}`
        );
      }
      return text ? (JSON.parse(text) as SessionResponse) : undefined;
    },
    [createDpopProof, normalizedSessionServerUrl]
  );

  const bootstrapSession = React.useCallback(async () => {
    if (bootstrapPromiseRef.current) {
      return bootstrapPromiseRef.current;
    }
    const task = (async () => {
      const tenantId = props.tenantId.trim();
      if (!tenantId) {
        throw new Error("SessionBasedFiinderProvider requires a tenantId");
      }
      const apiBaseUrl = trimmedFiinderApiUrl;
      if (!apiBaseUrl) {
        throw new Error("SessionBasedFiinderProvider requires fiinderApiUrl");
      }
      const userId = props.userId.trim();
      if (!userId) {
        throw new Error("SessionBasedFiinderProvider requires a userId");
      }
      const keyPair = await ensureKeyPair();
      const data = await callSessionEndpoint("/session/create", "POST", {
        body: {
          tenantId,
          apiBaseUrl,
          userId,
          publicJwk: keyPair.publicJwk,
        },
      });
      if (data) {
        return await applySessionResponse(data);
      }
      return null;
    })();
    const chained = task
      .then((result) => {
        bootstrapPromiseRef.current = null;
        bootstrapPromiseRegistry.delete(bootstrapKey);
        return result;
      })
      .catch((error) => {
        bootstrapPromiseRef.current = null;
        bootstrapPromiseRegistry.delete(bootstrapKey);
        throw error;
      });
    bootstrapPromiseRef.current = chained;
    bootstrapPromiseRegistry.set(bootstrapKey, chained);
    try {
      return await bootstrapPromiseRef.current;
    } catch (error) {
      if (error instanceof ThumbprintMismatchError) {
        console.error(
          "[SessionBasedFiinderProvider] Thumbprint mismatch on bootstrap, regenerating key pair",
          error
        );
        keyPairRef.current = null;
        sessionRef.current = null;
        setSession(null);
        resetAuthStrategy();
        clearWebsocketConfiguration();
        clearRefreshTimer();
        bootstrapPromiseRef.current = null;
        bootstrapPromiseRegistry.delete(bootstrapKey);
        return bootstrapSession();
      }
      console.error(
        "[SessionBasedFiinderProvider] Session bootstrap failed",
        error
      );
      throw error;
    }
  }, [
    applySessionResponse,
    callSessionEndpoint,
    clearRefreshTimer,
    ensureKeyPair,
    trimmedFiinderApiUrl,
    props.tenantId,
    props.userId,
    resetAuthStrategy,
    bootstrapKey,
  ]);

  const refreshSession = React.useCallback(async () => {
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }
    const current = sessionRef.current;
    if (!current) return null;
    const task = (async () => {
      const refreshHtuBase = normalizedFiinderApiUrl || normalizedSessionServerUrl;
      const data = await callSessionEndpoint(
        "/session/refresh",
        "POST",
        {
          body: {
            apiBaseUrl: props.fiinderApiUrl,
          },
          accessTokenForProof: current.accessToken,
          htuOverride: joinUrl(refreshHtuBase, "/session/refresh"),
        }
      );
      if (data) {
        return await applySessionResponse(data);
      }
      return null;
    })();
    const chained = task
      .then((result) => {
        refreshPromiseRef.current = null;
        refreshPromiseRegistry.delete(bootstrapKey);
        return result;
      })
      .catch((error) => {
        refreshPromiseRef.current = null;
        refreshPromiseRegistry.delete(bootstrapKey);
        throw error;
      });
    refreshPromiseRef.current = chained;
    refreshPromiseRegistry.set(bootstrapKey, chained);
    try {
      return await refreshPromiseRef.current;
    } catch (error) {
      if (error instanceof ThumbprintMismatchError) {
        console.error(
          "[SessionBasedFiinderProvider] Thumbprint mismatch on refresh, restarting session",
          error
        );
        keyPairRef.current = null;
        sessionRef.current = null;
        setSession(null);
        resetAuthStrategy();
        clearWebsocketConfiguration();
        clearRefreshTimer();
        bootstrapPromiseRef.current = null;
        bootstrapPromiseRegistry.delete(bootstrapKey);
        return bootstrapSession();
      }
      console.error(
        "[SessionBasedFiinderProvider] Session refresh failed",
        error
      );
      clearRefreshTimer();
      throw error;
    }
  }, [
    applySessionResponse,
    bootstrapSession,
    callSessionEndpoint,
    clearRefreshTimer,
    props.fiinderApiUrl,
    normalizedFiinderApiUrl,
    normalizedSessionServerUrl,
    resetAuthStrategy,
    bootstrapKey,
  ]);

  const scheduleRefresh = React.useCallback(
    (expiresInSeconds: number) => {
      if (!isBrowser()) return;
      clearRefreshTimer();
      if (!Number.isFinite(expiresInSeconds) || expiresInSeconds <= 0) {
        return;
      }
      const delayMs = Math.max(
        1000,
        (expiresInSeconds - REFRESH_MARGIN_SECONDS) * 1000
      );
      refreshTimerRef.current = window.setTimeout(() => {
        refreshSession().catch((error) => {
          console.error(
            "[SessionBasedFiinderProvider] Scheduled refresh failed",
            error
          );
        });
      }, delayMs);
    },
    [clearRefreshTimer, refreshSession]
  );

  React.useEffect(() => {
    if (!isBrowser()) return;
    sessionRef.current = null;
    setSession(null);
    resetAuthStrategy();
    clearRefreshTimer();
    bootstrapSession().catch((error) => {
      console.error(
        "[SessionBasedFiinderProvider] Failed to bootstrap session",
        error
      );
    });
    return () => {
      clearRefreshTimer();
    };
  }, [
    bootstrapSession,
    clearRefreshTimer,
    normalizedSessionServerUrl,
    props.fiinderApiUrl,
    props.tenantId,
    props.userId,
  ]);

  React.useEffect(() => {
    if (!session) return;
    scheduleRefresh(session.expiresIn);
  }, [scheduleRefresh, session]);

  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      clearRefreshTimer();
      resetAuthStrategy();
      clearWebsocketConfiguration();
    };
  }, [clearRefreshTimer]);

  const personationProps = React.useMemo<PersonationProps>(
    () => ({
      fiinderApiUrl: props.fiinderApiUrl,
      companyToken: session?.accessToken ?? "",
      agentName: props.agentName,
      agentRole: props.agentRole,
      agentAvatarUrl: props.agentAvatarUrl,
      companyMainColor: props.companyMainColor,
      introMessage: props.introMessage,
      introMessageMeta: props.introMessageMeta,
      promptSuggestionsTitle: props.promptSuggestionsTitle,
      promptSuggestions: props.promptSuggestions,
      launcherCallToAction: props.launcherCallToAction,
      launcherAvatarSize: props.launcherAvatarSize,
      linkTarget: props.linkTarget,
      forceTopLayer: props.forceTopLayer,
    }),
    [
      props.agentAvatarUrl,
      props.agentName,
      props.agentRole,
      props.companyMainColor,
      props.fiinderApiUrl,
      props.introMessage,
      props.introMessageMeta,
      props.launcherAvatarSize,
      props.launcherCallToAction,
      props.linkTarget,
      props.forceTopLayer,
      props.promptSuggestions,
      props.promptSuggestionsTitle,
      session?.accessToken,
    ]
  );

  const personation = React.useMemo(
    () => toPersonationValue(personationProps),
    [personationProps]
  );

  React.useEffect(() => {
    assignGlobals(personationProps);
  }, [personationProps]);

  const apiRef = React.useRef<{ api: FiinderApi; key: string } | null>(null);
  const apiKey = props.fiinderApiUrl;

  if (session && (apiRef.current === null || apiRef.current.key !== apiKey)) {
    const wsConnect = initWebSocket();
    apiRef.current = {
      api: getFiinderApi(wsConnect),
      key: apiKey,
    };
  }

  if (!session || !apiRef.current) {
    return null;
  }

  return (
    <PersonationContext.Provider value={personation}>
      <ApiProvider api={apiRef.current.api}>{props.children}</ApiProvider>
    </PersonationContext.Provider>
  );
}
